/**
 * AST -> control-flow graph.
 *
 * The compiler threads a list of **open exits** through the statement list. An
 * exit is a dangling edge waiting for a target: `{ nodeId, port, label, type }`.
 * Emitting a statement consumes the incoming exits (wiring them to whatever
 * node it creates) and returns the exits that leave it. A branch simply returns
 * more than one exit; convergence is what happens when several exits are handed
 * to the same next statement, so it needs no special case.
 *
 * `break`, `continue` and `return` do not become nodes. They redirect their
 * incoming exits into the enclosing loop's or function's collection points, and
 * return no exits of their own — which is exactly why the statement after them
 * ends up with no incoming edge, matching the "unreachable code" diagnostic.
 *
 * Node ids come from the AST's stable ids, so an edit that does not move a
 * statement leaves its node id — and therefore its React key and its screen
 * position — untouched.
 */
import type {
  Block,
  FunctionDeclaration,
  IfStatement,
  Program,
  Statement,
  SwitchStatement,
  TerminatorLabelStyle,
} from '../../language/ast/nodes';
import { collectFunctions, mainFlow, terminatorLabel } from '../../language/ast/nodes';
import { deriveId } from '../../language/ast/ids';
import type { SourceRange } from '../../language/ast/source';
import type { SemanticInfo } from '../../language/semantic/analyze';
import type {
  EdgeType,
  FlowDirection,
  FlowGraph,
  GraphEdge,
  GraphGroup,
  GraphNode,
  NodeRole,
  NodeShape,
  PortSide,
} from '../model/types';
import { indexGraph, shapeForRole } from '../model/types';
import type { SizingOptions } from '../model/measure';
import { measureNode } from '../model/measure';

export interface CompileOptions {
  readonly direction: FlowDirection;
  /** Default treatment for `call`. Per-function overrides win over this. */
  readonly functionDisplay: 'expanded' | 'collapsed';
  /** Normalized function names whose display is flipped from the default. */
  readonly functionOverrides: ReadonlyMap<string, 'expanded' | 'collapsed'>;
  readonly yesLabel: string;
  readonly noLabel: string;
  readonly terminatorLabelStyle: TerminatorLabelStyle;
  /** Draw declared-but-never-called functions as standalone subgraphs. */
  readonly showUnusedFunctions: boolean;
  /** Hard cap; compilation stops and flags `truncated` past this. */
  readonly maxNodes: number;
  /** How many nested `call` expansions are allowed before falling back to collapsed. */
  readonly maxExpansionDepth: number;
  readonly sizing: Partial<SizingOptions>;
}

export const DEFAULT_COMPILE_OPTIONS: CompileOptions = {
  direction: 'top-to-bottom',
  functionDisplay: 'expanded',
  functionOverrides: new Map(),
  yesLabel: 'Yes',
  noLabel: 'No',
  terminatorLabelStyle: 'full-phrase',
  showUnusedFunctions: true,
  maxNodes: 4000,
  maxExpansionDepth: 3,
  sizing: {},
};

/** A dangling edge waiting for its target. */
interface Exit {
  readonly nodeId: string;
  readonly port: PortSide;
  readonly label: string;
  readonly type: EdgeType;
  readonly range: SourceRange | null;
  readonly isBackEdge: boolean;
}

interface LoopFrame {
  /** Where `continue` goes. */
  readonly continueTarget: string;
  readonly continuePort: PortSide;
  /** Exits collected from `break`, wired up after the loop. */
  readonly breakExits: Exit[];
}

interface FunctionFrame {
  /** Exits collected from `return`, wired to the function's exit point. */
  readonly returnExits: Exit[];
}

export interface CompileResult {
  readonly graph: FlowGraph;
  /** Node id -> the source range that produced it, for click-to-code. */
  readonly nodeRanges: ReadonlyMap<string, SourceRange>;
}

export function compileProgram(
  program: Program,
  semantics: SemanticInfo,
  options: Partial<CompileOptions> = {},
): CompileResult {
  const opts: CompileOptions = { ...DEFAULT_COMPILE_OPTIONS, ...options };
  const compiler = new Compiler(program, semantics, opts);
  return compiler.run();
}

class Compiler {
  private readonly nodes: GraphNode[] = [];
  private readonly edges: GraphEdge[] = [];
  private readonly groups: GraphGroup[] = [];
  private readonly nodeIds = new Set<string>();
  private readonly nodeRanges = new Map<string, SourceRange>();
  private readonly usedEdgeIds = new Set<string>();

  private readonly loopStack: LoopFrame[] = [];
  private readonly functionStack: FunctionFrame[] = [];
  /** Normalized names currently being expanded — the recursion guard. */
  private readonly expansionStack: string[] = [];
  private groupStack: string[] = [];
  private depth = 0;
  private truncated = false;

  constructor(
    private readonly program: Program,
    private readonly semantics: SemanticInfo,
    private readonly options: CompileOptions,
  ) {}

  run(): CompileResult {
    const flow = mainFlow(this.program);
    this.emitStatements(flow, []);

    if (this.options.showUnusedFunctions) {
      for (const declaration of collectFunctions(this.program)) {
        if (this.semantics.reachableFunctions.has(declaration.normalizedName)) {
          // Reachable functions already appear, inlined at their call sites in
          // expanded mode. In collapsed mode they still deserve a subgraph.
          if (this.displayFor(declaration.normalizedName) === 'expanded') continue;
        }
        this.emitStandaloneFunction(declaration);
      }
    }

    const graph: FlowGraph = indexGraph({
      nodes: this.nodes,
      edges: this.edges,
      groups: this.groups,
      direction: this.options.direction,
      bounds: null,
      truncated: this.truncated,
    });

    return { graph, nodeRanges: this.nodeRanges };
  }

  /* ---------------------------------------------------------------- *
   * Node & edge construction
   * ---------------------------------------------------------------- */

  private get atNodeLimit(): boolean {
    return this.nodes.length >= this.options.maxNodes;
  }

  /** Guarantees uniqueness even if two AST nodes somehow hash alike. */
  private uniqueId(preferred: string): string {
    if (!this.nodeIds.has(preferred)) return preferred;
    let n = 2;
    while (this.nodeIds.has(`${preferred}~${n}`)) n += 1;
    return `${preferred}~${n}`;
  }

  private addNode(input: {
    id: string;
    role: NodeRole;
    label: string;
    range: SourceRange | null;
    metadata?: GraphNode['metadata'];
    shapeOverride?: NodeShape;
    calleeName?: string;
    functionName?: string;
  }): GraphNode | null {
    if (this.atNodeLimit) {
      this.truncated = true;
      return null;
    }

    const metadata = input.metadata ?? {};
    // An explicit `@id(...)` wins, so users can write stable anchors by hand.
    const id = this.uniqueId(metadata.id ?? input.id);
    const shape: NodeShape = metadata.shape ?? input.shapeOverride ?? shapeForRole(input.role);
    const { lines, size } = measureNode(
      input.label,
      input.role,
      shape,
      metadata.icon !== undefined,
      this.options.sizing,
    );

    const node: GraphNode = {
      id,
      role: input.role,
      shape,
      label: input.label,
      lines,
      range: input.range,
      incoming: [],
      outgoing: [],
      metadata,
      size,
      position: { x: 0, y: 0 },
      groupId: this.groupStack[this.groupStack.length - 1] ?? null,
      depth: this.depth,
      ...(input.calleeName !== undefined ? { calleeName: input.calleeName } : {}),
      ...(input.functionName !== undefined ? { functionName: input.functionName } : {}),
    };

    this.nodeIds.add(id);
    this.nodes.push(node);
    if (input.range) this.nodeRanges.set(id, input.range);

    const group = this.groupStack[this.groupStack.length - 1];
    if (group) {
      const index = this.groups.findIndex((g) => g.id === group);
      if (index >= 0) {
        const existing = this.groups[index]!;
        this.groups[index] = { ...existing, nodeIds: [...existing.nodeIds, id] };
      }
    }

    return node;
  }

  private connect(exits: readonly Exit[], targetId: string, targetPort: PortSide): void {
    for (const exit of exits) {
      const base = `${exit.nodeId}->${targetId}`;
      let id = base;
      let n = 2;
      while (this.usedEdgeIds.has(id)) {
        id = `${base}~${n}`;
        n += 1;
      }
      this.usedEdgeIds.add(id);

      this.edges.push({
        id,
        source: exit.nodeId,
        target: targetId,
        sourcePort: exit.port,
        targetPort,
        label: exit.label,
        type: exit.type,
        points: [],
        labelPosition: null,
        range: exit.range,
        isBackEdge: exit.isBackEdge,
      });
    }
  }

  /** Where an edge leaves a node, given the flow direction. */
  private get forwardOut(): PortSide {
    return this.options.direction === 'top-to-bottom' ? 'bottom' : 'right';
  }

  private get forwardIn(): PortSide {
    return this.options.direction === 'top-to-bottom' ? 'top' : 'left';
  }

  /** The side a decision's secondary branch leaves from. */
  private get branchOut(): PortSide {
    return this.options.direction === 'top-to-bottom' ? 'right' : 'bottom';
  }

  private get backIn(): PortSide {
    return this.options.direction === 'top-to-bottom' ? 'left' : 'top';
  }

  private exitOf(
    node: GraphNode,
    label = '',
    type: EdgeType = 'flow',
    port: PortSide = this.forwardOut,
  ): Exit {
    return { nodeId: node.id, port, label, type, range: node.range, isBackEdge: false };
  }

  /* ---------------------------------------------------------------- *
   * Statements
   * ---------------------------------------------------------------- */

  private emitStatements(statements: readonly Statement[], incoming: readonly Exit[]): Exit[] {
    let exits = [...incoming];
    for (const statement of statements) {
      if (this.atNodeLimit) {
        // Bail out before emitting the rest, and say so — a silently short
        // diagram would read as a correct one.
        this.truncated = true;
        break;
      }
      exits = this.emitStatement(statement, exits);
    }
    return exits;
  }

  private emitBlock(block: Block, incoming: readonly Exit[]): Exit[] {
    return this.emitStatements(block.statements, incoming);
  }

  private emitStatement(statement: Statement, incoming: readonly Exit[]): Exit[] {
    switch (statement.kind) {
      case 'ActionStatement':
        return this.emitSimple(
          statement.stableId,
          'action',
          statement.label.text,
          statement,
          incoming,
        );

      case 'StartStatement':
        return this.emitSimple(
          statement.stableId,
          'start',
          terminatorLabel(statement, this.options.terminatorLabelStyle),
          statement,
          incoming,
        );

      case 'EndStatement': {
        const node = this.addNode({
          id: statement.stableId,
          role: 'end',
          label: terminatorLabel(statement, this.options.terminatorLabelStyle),
          range: statement.range,
          metadata: statement.metadata,
        });
        if (!node) return [];
        this.connect(incoming, node.id, this.forwardIn);
        return []; // an end terminates the path
      }

      case 'ErrorStatement':
        return this.emitSimple(
          statement.stableId,
          'error',
          statement.text.trim() || 'Invalid statement',
          statement,
          incoming,
        );

      case 'CallStatement':
        return this.emitCall(statement, incoming);

      case 'IfStatement':
        return this.emitIf(statement, incoming);

      case 'WhileStatement':
        return this.emitWhile(statement, incoming);

      case 'RepeatStatement':
        return this.emitRepeat(statement, incoming);

      case 'ForEachStatement':
        return this.emitForEach(statement, incoming);

      case 'SwitchStatement':
        return this.emitSwitch(statement, incoming);

      case 'ReturnStatement': {
        const label = statement.label ? `return ${statement.label.text}` : 'return';
        const node = this.addNode({
          id: statement.stableId,
          role: 'return',
          label,
          range: statement.range,
          metadata: statement.metadata,
        });
        if (!node) return [];
        this.connect(incoming, node.id, this.forwardIn);

        const frame = this.functionStack[this.functionStack.length - 1];
        if (frame) {
          frame.returnExits.push(this.exitOf(node, '', 'return'));
          return [];
        }
        // A `return` outside a function ends the flow, which the semantic pass
        // has already warned about.
        return [];
      }

      case 'BreakStatement': {
        const frame = this.loopStack[this.loopStack.length - 1];
        if (frame) {
          frame.breakExits.push(
            ...incoming.map((exit) => ({
              ...exit,
              label: exit.label || 'break',
              type: 'break' as EdgeType,
            })),
          );
        }
        return [];
      }

      case 'ContinueStatement': {
        const frame = this.loopStack[this.loopStack.length - 1];
        if (frame) {
          this.connect(
            incoming.map((exit) => ({
              ...exit,
              label: exit.label || 'continue',
              type: 'continue' as EdgeType,
              isBackEdge: true,
            })),
            frame.continueTarget,
            frame.continuePort,
          );
        }
        return [];
      }
    }
  }

  private emitSimple(
    id: string,
    role: NodeRole,
    label: string,
    statement: Statement,
    incoming: readonly Exit[],
  ): Exit[] {
    const node = this.addNode({
      id,
      role,
      label,
      range: statement.range,
      metadata: statement.metadata,
    });
    if (!node) return [];
    this.connect(incoming, node.id, this.forwardIn);
    return [this.exitOf(node)];
  }

  /* --- decisions ---------------------------------------------------- */

  private emitIf(statement: IfStatement, incoming: readonly Exit[]): Exit[] {
    const decision = this.addNode({
      id: statement.stableId,
      role: 'decision',
      label: statement.condition.text || '?',
      range: statement.condition.range,
      metadata: statement.metadata,
    });
    if (!decision) return [];
    this.connect(incoming, decision.id, this.forwardIn);

    const yesLabel = statement.branchLabels.yes?.text ?? this.options.yesLabel;
    const noLabel = statement.branchLabels.no?.text ?? this.options.noLabel;

    this.depth += 1;
    const thenExits = this.emitBlock(statement.consequent, [
      this.exitOf(decision, yesLabel, 'true', this.forwardOut),
    ]);

    let elseExits: Exit[];
    const elseEntry = this.exitOf(decision, noLabel, 'false', this.branchOut);
    if (!statement.alternate) {
      elseExits = [elseEntry];
    } else if (statement.alternate.kind === 'Block') {
      elseExits = this.emitBlock(statement.alternate, [elseEntry]);
    } else {
      elseExits = this.emitStatement(statement.alternate, [elseEntry]);
    }
    this.depth -= 1;

    // A branch that ended explicitly contributes nothing to the convergence,
    // which is what keeps `end;` inside a branch from sprouting a stray edge.
    return [...thenExits, ...elseExits];
  }

  private emitSwitch(statement: SwitchStatement, incoming: readonly Exit[]): Exit[] {
    const decision = this.addNode({
      id: statement.stableId,
      role: 'decision',
      label: statement.discriminant.text || '?',
      range: statement.discriminant.range,
      metadata: statement.metadata,
    });
    if (!decision) return [];
    this.connect(incoming, decision.id, this.forwardIn);

    const frame: LoopFrame = {
      continueTarget: decision.id,
      continuePort: this.backIn,
      breakExits: [],
    };
    // `break` inside a switch leaves the switch, so it shares the loop frame
    // machinery even though a switch is not a loop.
    this.loopStack.push(frame);
    this.depth += 1;

    const exits: Exit[] = [];
    const total = statement.cases.length;

    statement.cases.forEach((branch, index) => {
      const isDefault = branch.test === null;
      const label = isDefault ? 'default' : branch.test!.text;
      // Fan the cases out: the middle one continues straight down, the rest
      // leave sideways, which keeps the diamond readable.
      const port = total === 1 || index === total - 1 ? this.forwardOut : this.branchOut;
      const entry: Exit = {
        nodeId: decision.id,
        port,
        label,
        type: isDefault ? 'default' : 'case',
        range: branch.range,
        isBackEdge: false,
      };
      exits.push(...this.emitBlock(branch.body, [entry]));
    });

    this.depth -= 1;
    this.loopStack.pop();

    return [...exits, ...frame.breakExits];
  }

  /* --- loops -------------------------------------------------------- */

  private emitWhile(
    statement: Extract<Statement, { kind: 'WhileStatement' }>,
    incoming: readonly Exit[],
  ): Exit[] {
    const decision = this.addNode({
      id: statement.stableId,
      role: 'loop',
      label: statement.condition.text || '?',
      range: statement.condition.range,
      metadata: statement.metadata,
    });
    if (!decision) return [];
    this.connect(incoming, decision.id, this.forwardIn);

    const yesLabel = statement.branchLabels.yes?.text ?? this.options.yesLabel;
    const noLabel = statement.branchLabels.no?.text ?? this.options.noLabel;

    const frame: LoopFrame = {
      continueTarget: decision.id,
      continuePort: this.backIn,
      breakExits: [],
    };
    this.loopStack.push(frame);
    this.depth += 1;

    const bodyExits = this.emitBlock(statement.body, [
      this.exitOf(decision, yesLabel, 'true', this.forwardOut),
    ]);

    this.depth -= 1;
    this.loopStack.pop();

    // Back edge: the body returns to the condition.
    this.connect(
      bodyExits.map((exit) => ({
        ...exit,
        type: 'loop-back' as EdgeType,
        isBackEdge: true,
        label: '',
      })),
      decision.id,
      this.backIn,
    );

    return [this.exitOf(decision, noLabel, 'loop-exit', this.branchOut), ...frame.breakExits];
  }

  private emitForEach(
    statement: Extract<Statement, { kind: 'ForEachStatement' }>,
    incoming: readonly Exit[],
  ): Exit[] {
    const label = statement.item
      ? `For each ${statement.item.text}`
      : `For each ${statement.header.text || '…'}`;

    const decision = this.addNode({
      id: statement.stableId,
      role: 'loop',
      label,
      range: statement.header.range,
      metadata: statement.metadata,
    });
    if (!decision) return [];
    this.connect(incoming, decision.id, this.forwardIn);

    const yesLabel = statement.branchLabels.yes?.text ?? 'Next item';
    const noLabel = statement.branchLabels.no?.text ?? 'Done';

    const frame: LoopFrame = {
      continueTarget: decision.id,
      continuePort: this.backIn,
      breakExits: [],
    };
    this.loopStack.push(frame);
    this.depth += 1;

    const bodyExits = this.emitBlock(statement.body, [
      this.exitOf(decision, yesLabel, 'true', this.forwardOut),
    ]);

    this.depth -= 1;
    this.loopStack.pop();

    this.connect(
      bodyExits.map((exit) => ({
        ...exit,
        type: 'loop-back' as EdgeType,
        isBackEdge: true,
        label: '',
      })),
      decision.id,
      this.backIn,
    );

    return [this.exitOf(decision, noLabel, 'loop-exit', this.branchOut), ...frame.breakExits];
  }

  private emitRepeat(
    statement: Extract<Statement, { kind: 'RepeatStatement' }>,
    incoming: readonly Exit[],
  ): Exit[] {
    // `repeat` runs its body before testing, so the body is emitted first and
    // the decision closes the loop underneath it.
    const entry = this.addNode({
      id: deriveId(statement.stableId, 'entry'),
      role: 'junction',
      label: '',
      range: statement.range,
    });
    if (!entry) return [];
    this.connect(incoming, entry.id, this.forwardIn);

    const decisionId = statement.stableId;
    const frame: LoopFrame = {
      continueTarget: decisionId,
      continuePort: this.backIn,
      breakExits: [],
    };
    this.loopStack.push(frame);
    this.depth += 1;

    const bodyExits = this.emitBlock(statement.body, [this.exitOf(entry)]);

    this.depth -= 1;
    this.loopStack.pop();

    const decision = this.addNode({
      id: decisionId,
      role: 'loop',
      label: statement.condition.text || '?',
      range: statement.condition.range,
      metadata: statement.metadata,
    });
    if (!decision) return frame.breakExits;
    this.connect(bodyExits, decision.id, this.forwardIn);

    const yesLabel = statement.branchLabels.yes?.text ?? this.options.yesLabel;
    const noLabel = statement.branchLabels.no?.text ?? this.options.noLabel;

    // `until (C)` keeps looping while C is false.
    this.connect(
      [
        {
          nodeId: decision.id,
          port: this.backIn,
          label: noLabel,
          type: 'loop-back',
          range: statement.condition.range,
          isBackEdge: true,
        },
      ],
      entry.id,
      this.backIn,
    );

    return [this.exitOf(decision, yesLabel, 'loop-exit', this.forwardOut), ...frame.breakExits];
  }

  /* --- calls & functions -------------------------------------------- */

  private displayFor(normalizedName: string): 'expanded' | 'collapsed' {
    return this.options.functionOverrides.get(normalizedName) ?? this.options.functionDisplay;
  }

  private emitCall(
    statement: Extract<Statement, { kind: 'CallStatement' }>,
    incoming: readonly Exit[],
  ): Exit[] {
    const normalized = statement.callee.text.toLowerCase();
    const declaration = this.semantics.functions.get(normalized);
    const argSuffix =
      statement.args && statement.args.length > 0
        ? `(${statement.args.map((a) => a.text).join(', ')})`
        : '';

    const node = this.addNode({
      id: statement.stableId,
      role: 'call',
      label: `${statement.callee.text}${argSuffix}`,
      range: statement.range,
      metadata: statement.metadata,
      calleeName: normalized,
    });
    if (!node) return [];
    this.connect(incoming, node.id, this.forwardIn);

    const callExit = this.exitOf(node);

    if (!declaration) return [callExit]; // unknown function — already an error
    if (this.displayFor(normalized) !== 'expanded') return [callExit];

    // Recursion guard. A function already on the expansion stack, or nesting
    // past the configured depth, falls back to the collapsed rendering and gets
    // a back edge so the cycle is still visible.
    if (this.expansionStack.includes(normalized)) return [callExit];
    if (this.expansionStack.length >= this.options.maxExpansionDepth) {
      this.truncated = true;
      return [callExit];
    }

    return this.expandFunction(declaration, callExit, node.id);
  }

  /** Inlines a function body inside a dashed frame, between call and continuation. */
  private expandFunction(
    declaration: FunctionDeclaration,
    entryExit: Exit,
    callNodeId: string,
  ): Exit[] {
    const groupId = `${callNodeId}:fn`;
    this.groups.push({
      id: groupId,
      label: declaration.name.text,
      functionName: declaration.normalizedName,
      nodeIds: [],
      bounds: null,
      range: declaration.range,
      depth: this.expansionStack.length,
    });

    this.expansionStack.push(declaration.normalizedName);
    this.groupStack.push(groupId);
    this.functionStack.push({ returnExits: [] });
    this.depth += 1;

    const bodyExits = this.emitBlock(declaration.body, [{ ...entryExit, type: 'call', label: '' }]);

    this.depth -= 1;
    const frame = this.functionStack.pop()!;
    this.groupStack.pop();
    this.expansionStack.pop();

    const exits = [...bodyExits, ...frame.returnExits];

    // An empty body would otherwise swallow the flow entirely.
    if (exits.length === 0 && bodyExits.length === 0 && frame.returnExits.length === 0) {
      return declaration.body.statements.length === 0 ? [entryExit] : [];
    }

    return exits.map((exit) => ({ ...exit, type: 'return' as EdgeType }));
  }

  /**
   * Draws a function as its own subgraph, disconnected from the main flow.
   * Used for functions that are never called, and for every function when
   * `functionDisplay` is `collapsed`.
   */
  private emitStandaloneFunction(declaration: FunctionDeclaration): void {
    if (this.atNodeLimit) return;

    const groupId = `${declaration.stableId}:standalone`;
    this.groups.push({
      id: groupId,
      label: declaration.name.text,
      functionName: declaration.normalizedName,
      nodeIds: [],
      bounds: null,
      range: declaration.range,
      depth: 0,
    });
    this.groupStack.push(groupId);
    this.expansionStack.push(declaration.normalizedName);
    this.functionStack.push({ returnExits: [] });

    const params =
      declaration.parameters && declaration.parameters.length > 0
        ? `(${declaration.parameters.map((p) => p.name).join(', ')})`
        : '';

    const entry = this.addNode({
      id: deriveId(declaration.stableId, 'entry'),
      role: 'function-entry',
      label: `${declaration.name.text}${params}`,
      range: declaration.name.range,
      metadata: declaration.metadata,
      functionName: declaration.normalizedName,
    });

    if (entry) {
      const bodyExits = this.emitBlock(declaration.body, [this.exitOf(entry)]);
      const frame = this.functionStack[this.functionStack.length - 1]!;
      const allExits = [...bodyExits, ...frame.returnExits];

      const exit = this.addNode({
        id: deriveId(declaration.stableId, 'exit'),
        role: 'function-exit',
        label: 'return',
        range: declaration.range,
        functionName: declaration.normalizedName,
      });
      if (exit) this.connect(allExits, exit.id, this.forwardIn);
    }

    this.functionStack.pop();
    this.expansionStack.pop();
    this.groupStack.pop();
  }
}
